import * as moment from "moment-timezone";
import { Constants } from "./Constants";
import { CurrentUserService } from "../services";
import { Functions } from "@hub/common/Functions";
import { eColumnType, eDateFormat } from "@hub/generated/enums";

export class DateFunctions {

    public static isDate(text: any): boolean {
        if (text == null) {
            return;
        }

        const isNumber = Functions.isNumber(text);
        if (
            new Date(text).toString() !== "Invalid Date" &&
            !!new Date(text) &&
            !/([a-zA-Z]{3})/.exec(text) &&
            (!isNumber || text > 2674800000) &&
            (typeof text === "string" && text.indexOf("%") === -1)
        ) {
            return true;
        } else {
            return false;
        }
    }

    public static fixTimeZone(date: Date, timeZone: string, time?: Date): Date {

        var dateTime: Date;

        if (time != null) {
            dateTime = new Date(date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate() + " " + time.getHours() + ":" + time.getMinutes());
        } else {
            dateTime = new Date(date);
        }

        var dateInTimeZone = new Date(dateTime).toLocaleString(Constants.DEFAULT_LOCALE, { timeZone: timeZone });

        var timeDifference: number = dateTime.getTime() - new Date(dateInTimeZone).getTime();

        dateTime.setTime(dateTime.getTime() + timeDifference);

        return dateTime;
    }

    public static getCombinedDate(date: Date, time: Date): Date {

        var dateTime: Date;

        dateTime = new Date(date.getFullYear() + "-" + (date.getMonth() + 1) + "-" + date.getDate() + " " + time.getHours() + ":" + time.getMinutes());
        
        return dateTime;
    }

    public static formatDate(date: Date): Date {
        return moment(date).toDate(); // format('MM/DD/YYYY');
    }

    public static formatDateFormat(date: Date): string {
        return moment(date).format('MM/DD/YYYY');
    }

    public static formatTimeFormat(date: Date): string {
        return moment(date).format('hh:mm A');
    }

    public static mmddyyyyDate(date: Date): string {
        return moment(date).format('mm dd yyyy');
    }

    public static dateFormat(date: Date): string {
        return moment(date).format('MMM DD YYYY'); // format('MMM d YYYY');
    }

    public static getUTCDate(): Date {
        return new Date(Date.now() + (new Date().getTimezoneOffset() * 60000))
    }

    public static toUTCDate(date: Date): Date {
        return new Date(date.getTime() - (date.getTimezoneOffset() * 60000))
    }

    // public static fixDate(date: Date): Date {
    //     let newDate = new Date(date);

    //     newDate.setHours(0, 0, 0, 0);
    //     let adjustedDate = new Date(
    //         newDate.setMinutes(-1 * newDate.getTimezoneOffset() + 12 * 60)
    //     );

    //     return adjustedDate;
    // }

    public static adjustDate(date, isToUserEffectiveTimeZone: boolean = false) {
        let givendate = new Date(date);
        givendate.setHours(0, 0, 0, 0);
        let adjustedDate = new Date(
            givendate.setMinutes(-1 * givendate.getTimezoneOffset() + (isToUserEffectiveTimeZone ? 12 * 60 : 0))
        );

        return adjustedDate;
    }

    public static adjustDatepickerDate(date) {
        let givendate = new Date(date);
        givendate.setHours(0, 0, 0, 0);
        let timezoneOffset = givendate.getTimezoneOffset();
        if (timezoneOffset < 0) {
            givendate = new Date(givendate.setMinutes(-timezoneOffset));
        } 
        
        return givendate;
    }

    public static sortByTimestamp(a: any, b: any): number {
        const x =
            a.dateCreatedTimestamp > 0
                ? new Date(a.dateCreatedTimestamp)
                : new Date(a.intervalStartTimestamp);
        const y =
            b.dateCreatedTimestamp > 0
                ? new Date(b.dateCreatedTimestamp)
                : new Date(b.intervalStartTimestamp);

        return x.getTime() - y.getTime();
    }

    public static dateStringToTimeZone(
        dateString: string,
        type: eColumnType,
        timeZone?: string,
        formatString?: string,
        isFormatString: boolean = true
    ): string {
        if (!dateString) {
            return "";
        }

        if (type == eColumnType.Time) {
            formatString = Constants.DEFAULT_FORMAT_TIME;
        }

        if (!formatString && isFormatString) {
            formatString = Constants.DEFAULT_FORMAT_DATE;
        }

        if (!timeZone) {
            timeZone = Constants.DEFAULT_TIMEZONE;
        }

        const date = moment.utc(dateString);
        let dateInUserTimeZone: any;

        if (!!timeZone) {
            dateInUserTimeZone = moment(date.valueOf()).tz(timeZone);
        }

        return dateInUserTimeZone.format(formatString);
    }

    public static dateToTimeZone(
        date: Date,
        timeZone?: string,
        type: eColumnType = eColumnType.Date,
        formatString?: string
    ): Date {

        return new Date(this.dateStringToTimeZone(this.formatDateToString(date, "LLL"), type, timeZone, formatString));
    }

    public static dateToUserTimeZone(
        dateString: string,
        type: eColumnType = eColumnType.Date,
        timeZone: string,
        formatString?: string,
        isFormatString: boolean = true
    ): string {

        return this.dateStringToTimeZone(dateString, type, timeZone, formatString, isFormatString);
    }

    public static toDateTimeString(date: string, timeZone: string, format: eDateFormat): string {
      var momentDate = moment(date);

      if (!!timeZone) {
        timeZone = Constants.DEFAULT_TIMEZONE
      }

      momentDate = momentDate.locale(timeZone);

      switch (+format) {
        case eDateFormat.Date:
          return momentDate.format("L");
        case eDateFormat.Time:
          return momentDate.format("LT");
        case eDateFormat.DaysAgo:
          return momentDate.fromNow();
        case eDateFormat.DatePlusDaysAgo:
          return `${momentDate.format("L")} (${momentDate.fromNow()})`;
        default:
          return momentDate.format("L LT");
      }
    }

    public static formatDateToTimeString(
        date: Date,
        formatString: string,
        locale: string = Constants.DEFAULT_LOCALE
    ): string {
        if (Functions.IsStringEmpty(formatString)) {
            formatString = Constants.DEFAULT_FORMAT_TIME;
        }
        let localized = moment(date).locale(locale);

        return localized.format(formatString);
    }

    public static formatDateToString(
        date: Date,
        formatString: string,
        locale: string = Constants.DEFAULT_LOCALE
    ): string {
        if (Functions.IsStringEmpty(formatString)) {
            formatString = 'LLL';
        }
        let localized = moment(date).locale(locale);

        return localized.format(formatString);
    }
}

export class CurentUser {
    constructor(public userService: CurrentUserService) { }
}
